import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useObOrderBookClearEvents, queryKeys } from '../api/hooks';
import './ManualInterventionsPage.css';

function formatTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtNum(x) {
  if (x == null || Number.isNaN(Number(x))) return '—';
  return Number(x).toFixed(4);
}

function ObRungClearsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hoursWindow, setHoursWindow] = useState(6);
  const [sourceFilter, setSourceFilter] = useState('');
  const [qualifyingOnly, setQualifyingOnly] = useState(true);

  const queryParams = useMemo(
    () => ({
      limit: 100,
      hours: hoursWindow > 0 ? hoursWindow : null,
      source: sourceFilter.trim() || null,
      qualifyingOnly,
    }),
    [hoursWindow, sourceFilter, qualifyingOnly]
  );

  const { data, isLoading, isError, error } = useObOrderBookClearEvents(queryParams);
  const events = useMemo(() => (Array.isArray(data?.events) ? data.events : []), [data]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.obOrderBookClearEvents(
        queryParams.limit,
        queryParams.hours,
        queryParams.source,
        queryParams.qualifyingOnly
      ),
    });
  }, [queryClient, queryParams]);

  const goToDashboard = useCallback(
    (marketKey) => {
      navigate(`/dashboard?market=${encodeURIComponent(marketKey)}`);
    },
    [navigate]
  );

  return (
    <div className="manual-interventions-page">
      <div className="page-header">
        <div>
          <h1>Alerts — OB ladder clears</h1>
          <p className="subtle" style={{ margin: '0.35rem 0 0' }}>
            Up to 100 rows from <code>true_odds.order_book_clear_log</code> for this service&apos;s sport scope.
            The table is partitioned by <strong>sport</strong> and <strong>event time</strong> (not by source or
            action); use a time window for cheaper queries. <strong>Partial-depth</strong> audit rows are omitted.
            Slack uses <code>entries.ob_rung_clear_alerts_slack</code> on the process that performs the clear;
            set <code>entries.ob_rung_clear_slack_entries_only</code> to true to limit Slack to{' '}
            <code>source=entry</code> only (excludes manual).
          </p>
        </div>
        <button type="button" className="button" onClick={handleRefresh}>
          Refresh
        </button>
      </div>

      <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
          <label className="subtle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Time window
            <select
              value={hoursWindow}
              onChange={(e) => setHoursWindow(Number(e.target.value))}
              style={{ minWidth: 140 }}
            >
              <option value={6}>Last 6 hours</option>
              <option value={12}>Last 12 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 72 hours</option>
              <option value={0}>All (no cutoff)</option>
            </select>
          </label>
          <label className="subtle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Source
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{ minWidth: 120 }}
            >
              <option value="">All</option>
              <option value="entry">entry</option>
              <option value="manual">manual</option>
            </select>
          </label>
          <label className="subtle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={qualifyingOnly}
              onChange={(e) => setQualifyingOnly(e.target.checked)}
            />
            Ladder clears only (hide rows with <code>n_clears = 0</code> audit: refresh, append-bottom, etc.)
          </label>
        </div>
      </div>

      <div className="card">
        {isLoading && events.length === 0 ? (
          <div className="loading" style={{ padding: '1.5rem' }}>
            Loading…
          </div>
        ) : isError ? (
          <div style={{ padding: '1rem', color: '#f87171' }}>
            {error?.message || 'Failed to load clears'}
          </div>
        ) : events.length === 0 ? (
          <div className="subtle" style={{ padding: '1.5rem' }}>
            No OB clear rows in scope (writers must log clears; reporting has no entry OB traffic).
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Time (UTC)</th>
                  <th>Market</th>
                  <th>Sport</th>
                  <th>Source</th>
                  <th>Action</th>
                  <th>Side</th>
                  <th>Level</th>
                  <th>Rungs</th>
                  <th>Stake cleared</th>
                  <th>z²</th>
                  <th>Acted by</th>
                </tr>
              </thead>
              <tbody>
                {events.map((row) => (
                  <tr
                    key={row.id != null ? String(row.id) : `${row.market_key}-${row.event_ts}`}
                    className="clickable-row"
                    onClick={() => goToDashboard(row.market_key)}
                    title="Open in dashboard"
                  >
                    <td>{formatTs(row.event_ts)}</td>
                    <td
                      style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={row.market_key}
                    >
                      {row.market_key}
                    </td>
                    <td>{row.sport || '—'}</td>
                    <td>{row.source || '—'}</td>
                    <td>
                      <code>{row.ob_action || '—'}</code>
                    </td>
                    <td>{row.side || '—'}</td>
                    <td>{row.level_index != null ? row.level_index : '—'}</td>
                    <td>{row.n_clears != null ? row.n_clears : '—'}</td>
                    <td>{fmtNum(row.stake_cleared)}</td>
                    <td>{fmtNum(row.z2)}</td>
                    <td className="subtle">{row.acted_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ObRungClearsPage;
